/**
 * Centered pinned-country shortcuts + global prepared (🌍) + calendar.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as RadixTooltip from '@radix-ui/react-tooltip';

import { cn } from '@/lib/utils';

import { DraggableCountryPin } from './DraggableCountryPin';

function arrayMove<T>(arr: T[], from: number, to: number): T[] {
  const result = [...arr];
  const [removed] = result.splice(from, 1);
  result.splice(to, 0, removed);
  return result;
}

export function PinnedCountriesStrip({
  ordered,
  setOrder,
}: {
  ordered: { countryKey: string; countryName?: string }[];
  setOrder: (countryKeys: string[]) => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [draggedKey, setDraggedKey] = useState<string | null>(null);

  const itemKeys = useMemo(() => ordered.map((c) => c.countryKey), [ordered]);

  const handleDragEnd = useCallback(() => setDraggedKey(null), []);

  const handleDragStart = useCallback((e: React.DragEvent, key: string) => {
    setDraggedKey(key);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', key);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, targetKey: string) => {
      e.preventDefault();
      setDraggedKey(null);
      const fromKey = e.dataTransfer.getData('text/plain');
      if (!fromKey || fromKey === targetKey) return;
      const fromIdx = itemKeys.indexOf(fromKey);
      const toIdx = itemKeys.indexOf(targetKey);
      if (fromIdx === -1 || toIdx === -1) return;
      setOrder(arrayMove(itemKeys, fromIdx, toIdx));
    },
    [itemKeys, setOrder],
  );

  const handleNavigate = useCallback(
    (countryKey: string) => navigate(`/smswiser/${encodeURIComponent(countryKey)}`),
    [navigate],
  );

  const pinnedPreparedActive =
    location.pathname === '/smswiser/pinned-prepared' ||
    location.pathname.startsWith('/smswiser/pinned-prepared/');

  return (
    <div className="absolute left-1/2 flex -translate-x-1/2 items-center gap-0.5">
      <RadixTooltip.Provider delayDuration={300} skipDelayDuration={100}>
        <RadixTooltip.Root>
          <RadixTooltip.Trigger asChild>
            <button
              type="button"
              onClick={() => navigate('/smswiser/pinned-prepared')}
              className={cn(
                'mr-0.5 flex flex-col items-center justify-center rounded-md px-1.5 py-1.5',
                'min-w-[36px] select-none transition-colors hover:bg-muted hover:text-foreground',
                pinnedPreparedActive ? 'bg-muted text-foreground' : 'text-muted-foreground',
              )}
              aria-label="Prepared drops for all pinned countries"
            >
              <span className="text-[1.125rem] leading-none" aria-hidden>
                🌍
              </span>
            </button>
          </RadixTooltip.Trigger>
          <RadixTooltip.Portal>
            <RadixTooltip.Content
              side="top"
              sideOffset={6}
              avoidCollisions
              collisionPadding={12}
              className="z-[300] max-w-[220px] rounded-md border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md"
            >
              Prepared drops across all pinned countries (same work date).
            </RadixTooltip.Content>
          </RadixTooltip.Portal>
        </RadixTooltip.Root>

        {ordered.map((c) => (
          <DraggableCountryPin
            key={c.countryKey}
            c={c}
            onNavigate={handleNavigate}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDrop={handleDrop}
            isDragging={draggedKey === c.countryKey}
          />
        ))}

        <div className="mx-1 h-6 w-px bg-border" />

        <RadixTooltip.Root>
          <RadixTooltip.Trigger asChild>
            <button
              type="button"
              onClick={() => navigate('/calendar')}
              className={cn(
                'flex items-center justify-center rounded-md p-2',
                'select-none transition-colors hover:bg-muted hover:text-foreground',
                location.pathname === '/calendar' ? 'bg-muted text-foreground' : 'text-muted-foreground',
              )}
              aria-label="Calendar overview"
            >
              <span className="text-[1.125rem] leading-none" aria-hidden>
                📅
              </span>
            </button>
          </RadixTooltip.Trigger>
          <RadixTooltip.Portal>
            <RadixTooltip.Content
              side="top"
              sideOffset={6}
              avoidCollisions
              collisionPadding={12}
              className="z-[300] rounded-md border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md"
            >
              Calendar overview
            </RadixTooltip.Content>
          </RadixTooltip.Portal>
        </RadixTooltip.Root>
      </RadixTooltip.Provider>
    </div>
  );
}
